import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

interface HeaderProps {
  className?: string;
  forceDarkMode?: boolean;
}

export const Header = ({ className = "", forceDarkMode = false }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const publicNavItems = [
    { label: "Home", href: "/", isAnchor: false },
    { label: "Publisher werden", href: "#so-funktioniert-es", isAnchor: true },
    { label: "Funktionen", href: "#funktionen", isAnchor: true },
    { label: "Preise", href: "#preise", isAnchor: true },
    { label: "Kontakt", href: "/contact", isAnchor: false },
  ];

  const isActuallyDark = forceDarkMode || true; // Default to dark for landing page

  return (
    <header className={`sticky top-0 z-40 w-full bg-transparent backdrop-blur-md relative ${className}`}>
      <div className="container mx-auto px-4 relative z-20">
        <div className="flex h-16 items-center justify-between relative z-30">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-full bg-white p-1 flex items-center justify-center">
              <img
                src="/lovable-uploads/1960ff0f-8400-4424-b14a-073e7254caa8.png"
                alt="Audio Empire Logo"
                className="h-full w-full object-contain"
              />
            </div>
            <span className={`font-bold text-xl transition-colors duration-300 ${
              isActuallyDark ? 'text-white' : 'text-gray-900'
            }`}>Audio Empire</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 pt-1">
            {publicNavItems.map((item) => (
              item.isAnchor ? (
                <button
                  key={item.href}
                  onClick={() => {
                    const targetElement = document.querySelector(item.href);
                    if (targetElement) {
                      targetElement.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      // If not on homepage, navigate to homepage first
                      window.location.href = `/${item.href}`;
                    }
                  }}
                  className={`text-base font-medium transition-all duration-300 px-3 py-2 rounded-md border border-transparent hover:border-gray-300/40 ${
                    isActuallyDark
                      ? 'text-gray-300 hover:text-white hover:border-white/40'
                      : 'text-gray-600 hover:text-gray-900 hover:border-gray-300/40'
                  }`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-base font-medium transition-all duration-300 px-3 py-2 rounded-md border border-transparent hover:border-gray-300/40 ${
                    isActuallyDark
                      ? 'text-gray-300 hover:text-white hover:border-white/40'
                      : 'text-gray-600 hover:text-gray-900 hover:border-gray-300/40'
                  }`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4 pt-1">
            <Button
              variant="outline"
              className={`border transition-colors duration-300 ${
                isActuallyDark
                  ? 'text-white border-white/30 bg-white/10 hover:bg-white/20'
                  : 'text-white bg-gray-900 border-gray-900 hover:bg-gray-800'
              }`}
              asChild
            >
              <Link to="https://app.audioempire.ai/login">Login</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 transition-colors duration-300 ${
                isActuallyDark
                  ? 'text-white hover:bg-white/20'
                  : 'text-gray-900 hover:bg-gray-900/20'
              }`}
            >
              {mobileMenuOpen ? (
                <X className={`h-6 w-6 transition-colors duration-300 ${
                  isActuallyDark ? 'text-white' : 'text-gray-900'
                }`} />
              ) : (
                <Menu className={`h-6 w-6 transition-colors duration-300 ${
                  isActuallyDark ? 'text-white' : 'text-gray-900'
                }`} />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 backdrop-blur-md border-t bg-black/70 border-white/10">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {publicNavItems.map((item) => (
                item.isAnchor ? (
                  <button
                    key={item.href}
                    onClick={() => {
                      const targetElement = document.querySelector(item.href);
                      if (targetElement) {
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        // If not on homepage, navigate to homepage first
                        window.location.href = `/${item.href}`;
                      }
                      setMobileMenuOpen(false);
                    }}
                    className="block px-3 py-2 text-base font-medium text-white hover:text-gray-300 transition-colors text-left w-full"
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block px-3 py-2 text-base font-medium text-white hover:text-gray-300 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              ))}

              <div className="pt-4 space-y-2">
                <div className="flex justify-center px-2">
                  <Button variant="outline" className="w-3/4 text-white bg-gray-900 border-gray-900 hover:bg-gray-800" asChild>
                    <Link to="https://app.audioempire.ai/login" onClick={() => setMobileMenuOpen(false)}>
                      Login
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};